import React, { useEffect, useState, useRef, Suspense } from "react";
import { getImageUrl } from "../lib/utils";
import { Link } from "react-router-dom";
import { Stethoscope, Scissors, Droplets, ShieldPlus, HeartPulse, FlaskConical } from 'lucide-react';

// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
// Import required Swiper modules
import { Navigation, Pagination, Autoplay } from "swiper/modules";

// Import Radix UI components
import * as Separator from "@radix-ui/react-separator";
import { Button } from "../components/ui/button";

// Import React Query hooks
import {
  useHomepageSettings,
  useCategories,
  useFeaturedCategories,
  useBrands,
  useProductsByIds,
  useStats,
} from "../hooks/useHomepageQueries";

// Import custom section components
import FeaturedProductsSection from "../components/products/FeaturedProductsSection";
import NewArrivalsSection from "../components/products/NewArrivalsSection";
import FeaturedCategoriesSection from "../components/categories/FeaturedCategoriesSection";
import FeaturedCategoryProducts from "../components/categories/FeaturedCategoryProducts";
import CategorySections from "../components/categories/CategorySections";
import BrandsSection from "../components/brands/BrandsSection";
import BrandSections from "../components/brands/BrandSections";
import HomePageSkeleton from "../components/ui/HomePageSkeleton";
import HomePageErrorBoundary from "../components/error/HomePageErrorBoundary";
import { SEOHead } from '../components/seo';
const useCategorySwiperStyles = () => {
  useEffect(() => {
    if (typeof document !== "undefined") {
      const style = document.createElement("style");
      style.textContent = `
        .category-swiper {
          padding-bottom: 35px !important;
        }
        .category-swiper .swiper-pagination {
          bottom: 0 !important;
        }
        .category-swiper .swiper-slide {
          height: auto;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
};

// HomePage Content Component
const HomePageContent = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Apply custom swiper styles
  useCategorySwiperStyles();

  // React Query hooks
  const {
    data: homepageData,
    isLoading: isHomepageLoading,
    error: homepageError,
  } = useHomepageSettings();

  const {
    data: categoriesApiData,
    isLoading: isCategoriesLoading,
    error: categoriesError,
  } = useCategories();

  const {
    data: featuredCategoriesData,
    isLoading: isFeaturedCategoriesLoading,
    error: featuredCategoriesError,
  } = useFeaturedCategories();

  const {
    data: brands,
    isLoading: isBrandsLoading,
    error: brandsError,
  } = useBrands();

  const { data: stats } = useStats();

  // Trust badges row - scroll-triggered entrance + count-up animation
  const trustBadgesRef = useRef(null);
  const [isTrustBadgesVisible, setIsTrustBadgesVisible] = useState(false);

  useEffect(() => {
    const node = trustBadgesRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTrustBadgesVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Extract data from homepage settings
  const homepageSettings = homepageData?.settings || {
    featuredProductsTitle: "Featured Products",
    featuredProductsSubtitle: "Top-rated medical equipment and supplies",
    newArrivalsTitle: "New Arrivals",
    newArrivalsSubtitle: "Latest additions to our medical inventory",
    showNewsletter: true,
    showTestimonials: true,
    showBrands: true,
    newsletterTitle: "Subscribe for Health Updates",
    newsletterSubtitle:
      "Get the latest health tips, product updates and special offers directly to your inbox",
  };

  const heroSlides = homepageData?.heroSlides || [];
  const featuredCategories =
    homepageData?.featuredCategories &&
      homepageData.featuredCategories.length > 0
      ? homepageData.featuredCategories
      : featuredCategoriesData || [];

  // Extract category and brand sections from homepage data
  const categorySections = homepageData?.categorySections || [];
  const brandSections = homepageData?.brandSections || [];

  // Featured products query - add safety check for valid IDs
  const featuredProductIds =
    homepageData?.selectedProducts?.featured?.filter(
      (id) => id && typeof id === "string"
    ) || [];
  const {
    data: dynamicFeaturedProducts = [],
    isLoading: isFeaturedProductsLoading,
  } = useProductsByIds(featuredProductIds, featuredProductIds.length > 0);

  // New arrivals query - add safety check for valid IDs
  const newArrivalProductIds =
    homepageData?.selectedProducts?.newArrivals?.filter(
      (id) => id && typeof id === "string"
    ) || [];
  const { data: dynamicNewArrivals = [], isLoading: isNewArrivalsLoading } =
    useProductsByIds(newArrivalProductIds, newArrivalProductIds.length > 0);

  // Calculate overall loading state
  const isLoading = isHomepageLoading || isCategoriesLoading || isFeaturedCategoriesLoading || isBrandsLoading;

  // Check for any errors
  const hasErrors = homepageError || categoriesError || featuredCategoriesError || brandsError;

  // Prepare data with fallbacks for consistent prop passing
  const brandsData = brands || [];
  const featuredProductsData = dynamicFeaturedProducts || [];
  const newArrivalsData = dynamicNewArrivals || [];

  // Early return for errors (optional - can be removed if you want to show fallback data even on errors)
  if (hasErrors && !isLoading) {
    console.warn("Some data failed to load:", {
      homepageError,
      categoriesError,
      featuredCategoriesError,
      brandsError,
    });
    // Continue rendering with fallback data rather than showing error state
  }

  // Counts up from 0 to `target` once `start` becomes true
  const AnimatedCount = ({ target, start, suffix = "" }) => {
    const [display, setDisplay] = useState(0);

    useEffect(() => {
      if (!start) return;
      let frame;
      let startTime;
      const duration = 1200;

      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(Math.floor(eased * target));
        if (progress < 1) frame = requestAnimationFrame(step);
      };

      frame = requestAnimationFrame(step);
      return () => cancelAnimationFrame(frame);
    }, [start, target]);

    return <>{display.toLocaleString()}{suffix}</>;
  };

  // ProductCard component is now in a separate file

  // Newsletter Section Component with Radix UI
  const NewsletterSection = ({ title, subtitle }) => {
    return (
      <section className="bg-gradient-to-br from-admin-prussian-500 via-admin-indigo-500 to-admin-ucla-500 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl xs:text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              {title}
            </h2>
            <p className="text-base xs:text-lg text-admin-light-200 mb-8 sm:mb-10 leading-relaxed">
              {subtitle}
            </p>

            <form className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="w-full h-12 sm:h-14 px-4 sm:px-5 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  required
                />
              </div>
              <Button className="h-12 sm:h-14 px-6 sm:px-8 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-all duration-200 focus:ring-2 focus:ring-primary-300 focus:ring-offset-2 focus:ring-offset-admin-prussian-500">
                Subscribe Now
              </Button>
            </form>

            <Separator.Root className="h-px bg-admin-light-700 my-6 sm:my-8" />

            <p className="text-sm xs:text-base text-admin-light-300 leading-relaxed">
              By subscribing, you agree to our{" "}
              <Link
                to="/privacy"
                className="text-primary-300 hover:text-primary-200 underline transition-colors"
              >
                Privacy Policy
              </Link>{" "}
              and consent to receive updates from our company.
            </p>
          </div>
        </div>
      </section>
    );
  };

  // FeaturedCategoriesSection is now in a separate file

  // Import FeaturedProductsSection component
  // This component is now in a separate file


  // How to Order Section - interactive 3-scene walkthrough (browse/select,
  // checkout form, delivery map) ported from a standalone HTML/CSS/JS prototype.
  // The whole timeline is imperative DOM work (typing effects, an SVG path
  // animation, staggered class toggles) run inside one effect, scoped to this
  // section's own root so it can't collide with anything else on the page.
  const HowToOrderSection = () => {
    const sectionRef = useRef(null);
    const rootRef = useRef(null);

    // Inject the widget's CSS once
    useEffect(() => {
      if (typeof document === "undefined") return;
      let style = document.getElementById("how-to-order-interactive-styles");
      if (!style) {
        style = document.createElement("style");
        style.id = "how-to-order-interactive-styles";
        document.head.appendChild(style);
      }
      style.textContent = `
        .how-section-bg { background: linear-gradient(135deg, #eff6ff 0%, #ffffff 45%, #fff1f2 100%); width: 100%; }
        .how-root { padding: 2.5rem 1rem 4rem; width: 100%; max-width: 860px; margin: 0 auto; }
        .how-header { text-align: center; margin-bottom: 2.25rem; }
        .how-header h2 { font-size: 30px; font-weight: 800; color: #1B365D; margin: 0 0 10px; letter-spacing: -0.02em; }
        .how-header p { font-size: 15px; color: #6b7280; margin: 0; }

        .how-card { background: #ffffff; border-radius: 28px; padding: 2rem 1.5rem 2rem; box-shadow: none; border: 1px solid #e2e8f0; width: 100%; overflow: hidden; }

        .step-nav { display: flex; align-items: center; justify-content: center; gap: 0; margin-bottom: 3rem; }
        .step-dot-wrap { display: flex; flex-direction: column; align-items: center; gap: 8px; cursor: pointer; }
        .step-dot { width: 40px; height: 40px; border-radius: 50%; border: 2px solid #e2e8f0; background: #f8fafc; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; color: #94a3b8; transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1); box-shadow: 0 1px 2px rgba(15,23,42,0.04); }
        .step-dot.active { background: linear-gradient(135deg, #1B365D, #24467d); border-color: #1B365D; color: #fff; transform: scale(1.18); box-shadow: 0 8px 18px -6px rgba(27,54,93,0.55); }
        .step-dot.done { background: linear-gradient(135deg, #ca8a04, #eab308); border-color: #ca8a04; color: #fff; box-shadow: 0 4px 10px -4px rgba(202,138,4,0.5); }
        .step-label { font-size: 12.5px; color: #94a3b8; transition: color 0.3s; white-space: nowrap; font-weight: 500; }
        .step-label.active { color: #1B365D; font-weight: 700; }
        .step-connector { flex: 1; height: 3px; border-radius: 999px; background: #e2e8f0; max-width: 80px; position: relative; overflow: hidden; margin-bottom: 26px; }
        .step-connector-fill { position: absolute; top: 0; left: 0; height: 100%; width: 0%; background: linear-gradient(90deg, #ca8a04, #eab308); transition: width 0.6s ease; }

        /* No transform:scale() here on purpose - it doesn't participate in
           document flow, so the panel's actual height (which varies with how
           the description text wraps) could bleed into the section below at
           some viewport widths. The panel's own sizing is already responsive. */
        .panels-wrap { position: relative; }

        .step-panel { display: none; flex-direction: column; align-items: center; gap: 1.5rem; }
        .step-panel.active { display: flex; }

        .scene-box { width: 100%; max-width: 520px; height: 380px; background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%); border: 1px solid #eef1f5; border-radius: 18px; position: relative; overflow: hidden; box-shadow: 0 12px 28px -16px rgba(15,23,42,0.18); }
        .step-desc { text-align: center; }
        .step-desc strong { display: block; font-size: 17px; font-weight: 700; color: #111827; margin-bottom: 6px; }
        .step-desc span { font-size: 14px; color: #6b7280; line-height: 1.5; }

        .auto-bar { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 0.5rem; }
        .auto-dot { width: 6px; height: 6px; border-radius: 999px; background: #d1d5db; transition: all 0.3s; cursor: pointer; }
        .auto-dot.active { background: #1B365D; width: 20px; }

        /* Scene 1: Browse & Select */
        .s1-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; padding: 45px 18px 18px; }
        .s1-card { background: #ffffff; border: 1px solid #eef1f5; border-radius: 12px; padding: 10px 8px; display: flex; flex-direction: column; align-items: center; gap: 4px; opacity: 0; transform: translateY(10px); transition: border-color 0.3s, transform 0.3s, box-shadow 0.3s; box-shadow: 0 1px 2px rgba(15,23,42,0.03); }
        .s1-card.show { opacity: 1; transform: translateY(0); }
        .s1-card.hovered { border-color: #1B365D; transform: scale(1.05); box-shadow: 0 10px 20px -10px rgba(27,54,93,0.35); }
        .s1-card.selected { border-color: #ca8a04; background: #fffbeb; box-shadow: 0 10px 20px -10px rgba(202,138,4,0.35); }
        .s1-icon { font-size: 22px; }
        .s1-name { font-size: 10px; color: #6b7280; }
        .s1-price { font-size: 11px; font-weight: 700; color: #111827; }
        .s1-cursor { position: absolute; width: 18px; height: 18px; pointer-events: none; transition: left 0.5s cubic-bezier(0.4,0,0.2,1), top 0.5s cubic-bezier(0.4,0,0.2,1); }
        .s1-cursor svg { filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2)); }
        .s1-cart-badge { position: absolute; top: 15px; right: 18px; background: linear-gradient(135deg, #1B365D, #24467d); color: #fff; font-size: 11px; font-weight: 700; border-radius: 999px; padding: 4px 11px; display: flex; align-items: center; gap: 5px; box-shadow: 0 4px 10px -4px rgba(27,54,93,0.5); z-index: 10; }
        .s1-badge-count { display: inline-block; transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1); }
        .s1-badge-count.bump { transform: scale(1.5); }

        /* Scene 2: Checkout */
        #s2box { display: flex; flex-direction: column; justify-content: center; }
        .s2-form { padding: 18px 22px; display: flex; flex-direction: column; gap: 12px; width: 100%; box-sizing: border-box; }
        .s2-row { display: flex; flex-direction: column; gap: 5px; }
        .s2-label { font-size: 11px; color: #6b7280; font-weight: 500; }
        .s2-field { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 11px; font-size: 13px; color: #111827; min-height: 30px; transition: border-color 0.3s, box-shadow 0.3s; position: relative; }
        .s2-field.typing { border-color: #1B365D; box-shadow: 0 0 0 3px rgba(27,54,93,0.08); }
        .s2-cursor-blink { display: inline-block; width: 1px; height: 13px; background: #1B365D; margin-left: 1px; vertical-align: middle; animation: howToBlink 0.8s step-end infinite; }
        @keyframes howToBlink { 50% { opacity: 0; } }
        .s2-methods { display: flex; gap: 8px; }
        .s2-method { border: 1px solid #e2e8f0; border-radius: 8px; padding: 6px 11px; font-size: 11px; color: #6b7280; cursor: pointer; transition: all 0.3s; font-weight: 500; }
        .s2-method.chosen { border-color: #dc2626; background: #fef2f2; color: #dc2626; box-shadow: 0 0 0 3px rgba(220,38,38,0.08); }
        .s2-btn { background: linear-gradient(135deg, #dc2626, #b91c1c); color: #fff; border: none; border-radius: 10px; padding: 10px; font-size: 13px; font-weight: 700; text-align: center; cursor: pointer; transition: transform 0.1s, opacity 0.2s, box-shadow 0.2s; margin-top: 4px; box-shadow: 0 8px 18px -8px rgba(220,38,38,0.55); }
        .s2-btn.clicked { transform: scale(0.96); }
        .s2-success { position: absolute; inset: 0; background: #ffffff; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; opacity: 0; transition: opacity 0.5s; pointer-events: none; }
        .s2-success.show { opacity: 1; }
        .s2-check { width: 52px; height: 52px; border-radius: 50%; background: linear-gradient(135deg, #fef9c3, #fef3c7); border: 2px solid #ca8a04; display: flex; align-items: center; justify-content: center; font-size: 22px; transform: scale(0); transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); box-shadow: 0 8px 18px -8px rgba(202,138,4,0.4); }
        .s2-success.show .s2-check { transform: scale(1); }
        .s2-success p { font-size: 13.5px; font-weight: 700; color: #ca8a04; margin: 0; }

        /* Scene 3: Delivery */
        .s3-scene { width: 100%; height: 100%; position: relative; }
        .s3-map { position: absolute; inset: 0; }
        .s3-status-bar { position: absolute; bottom: 0; left: 0; right: 0; background: #f8fafc; border-top: 1px solid #eef1f5; padding: 10px 16px; display: flex; align-items: center; gap: 10px; }
        .s3-status-item { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #9ca3af; transition: color 0.4s; font-weight: 500; }
        .s3-status-item.done { color: #ca8a04; }
        .s3-status-item.active { color: #1B365D; font-weight: 700; }
        .s3-dot-s { width: 6px; height: 6px; border-radius: 50%; background: #d1d5db; transition: background 0.4s; }
        .s3-status-item.done .s3-dot-s { background: #ca8a04; }
        .s3-status-item.active .s3-dot-s { background: #1B365D; }
        .s3-arrow { color: #d1d5db; font-size: 10px; }

        @media (prefers-reduced-motion: reduce) {
          .how-root *, .how-root *::before, .how-root *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }
      `;
      document.head.appendChild(style);
    }, []);

    // Drive the whole widget imperatively, scoped to this section's root
    useEffect(() => {
      const root = rootRef.current;
      const section = sectionRef.current;
      if (!root || !section) return;

      const q = (sel) => root.querySelector(sel);

      let current = 0;
      let autoTimer = null;
      let sceneTimers = [];
      let cartCount = 0;
      let started = false;

      const clearSceneTimers = () => { sceneTimers.forEach(clearTimeout); sceneTimers = []; };
      const st = (fn, ms) => { sceneTimers.push(setTimeout(fn, ms)); };

      function goTo(idx) {
        clearSceneTimers();
        if (autoTimer) clearInterval(autoTimer);

        [0, 1, 2].forEach((i) => {
          q('#panel' + i).classList.remove('active');
          const dot = q('#dot' + i);
          const lbl = q('#lbl' + i);
          dot.classList.remove('active', 'done');
          lbl.classList.remove('active');
          q('#adot' + i).classList.remove('active');
          if (i < idx) { dot.classList.add('done'); dot.textContent = '✓'; }
          else { dot.textContent = i + 1; }
        });
        q('#fill0').style.width = idx > 0 ? '100%' : '0%';
        q('#fill1').style.width = idx > 1 ? '100%' : '0%';

        q('#panel' + idx).classList.add('active');
        q('#dot' + idx).classList.add('active');
        q('#lbl' + idx).classList.add('active');
        q('#adot' + idx).classList.add('active');
        current = idx;

        if (idx === 0) runScene1();
        if (idx === 1) runScene2();
        if (idx === 2) runScene3();

        autoTimer = setInterval(() => { goTo((current + 1) % 3); }, 6000);
      }

      function runScene1() {
        cartCount = 0;
        q('#cartCount').textContent = '0';
        const cards = [0, 1, 2, 3, 4, 5];
        cards.forEach((i) => q('#c' + i).classList.remove('show', 'hovered', 'selected'));

        const cursor = q('#s1cursor');
        cursor.style.transition = 'none';
        cursor.style.left = '60px'; cursor.style.top = '60px';

        cards.forEach((i, j) => { st(() => { q('#c' + i).classList.add('show'); }, 100 + j * 100); });

        function hoverCard(cardIdx, cx, cy, sel, delay) {
          st(() => {
            cursor.style.transition = 'left 0.5s cubic-bezier(0.4,0,0.2,1), top 0.5s cubic-bezier(0.4,0,0.2,1)';
            cursor.style.left = cx + 'px'; cursor.style.top = cy + 'px';
            st(() => { q('#c' + cardIdx).classList.add('hovered'); }, 400);
            if (sel) {
              st(() => {
                q('#c' + cardIdx).classList.remove('hovered');
                q('#c' + cardIdx).classList.add('selected');
                cartCount++;
                const badge = q('#cartCount');
                badge.textContent = cartCount;
                badge.classList.add('bump');
                st(() => badge.classList.remove('bump'), 300);
              }, 800);
            } else {
              st(() => q('#c' + cardIdx).classList.remove('hovered'), 800);
            }
          }, delay);
        }

        hoverCard(1, 165, 82, false, 900);
        hoverCard(2, 280, 82, true, 1800);
        hoverCard(4, 165, 162, true, 3200);
        hoverCard(0, 50, 82, true, 4500);
      }

      function runScene2() {
        q('#s2name').innerHTML = '';
        q('#s2card').innerHTML = '';
        q('#pm0').classList.remove('chosen');
        q('#pm1').classList.remove('chosen');
        q('#s2btn').classList.remove('clicked');
        q('#s2success').classList.remove('show');

        function typeInto(elId, text, delay, cb) {
          const el = q('#' + elId);
          el.classList.add('typing');
          el.innerHTML = '<span class="s2-cursor-blink"></span>';
          let i = 0;
          function typeChar() {
            if (i < text.length) {
              el.innerHTML = text.slice(0, i + 1) + '<span class="s2-cursor-blink"></span>';
              i++;
              sceneTimers.push(setTimeout(typeChar, 55 + Math.random() * 30));
            } else {
              el.classList.remove('typing');
              el.innerHTML = text;
              if (cb) cb();
            }
          }
          sceneTimers.push(setTimeout(typeChar, delay));
        }

        typeInto('s2name', 'Aarav Shrestha', 400, null);
        typeInto('s2card', '+977 98XXXXXXXX', 1800, () => {
          st(() => {
            q('#pm0').classList.add('chosen');
            st(() => {
              q('#s2btn').classList.add('clicked');
              st(() => {
                q('#s2btn').classList.remove('clicked');
                st(() => { q('#s2success').classList.add('show'); }, 150);
              }, 200);
            }, 500);
          }, 500);
        });
      }

      function runScene3() {
        const route = q('#routePath');
        const van = q('#vanGroup');
        const pin = q('#destPin');
        const statuses = ['ss0', 'ss1', 'ss2', 'ss3'];
        statuses.forEach((id) => q('#' + id).classList.remove('done', 'active'));
        q('#ss0').classList.add('active');
        route.style.strokeDashoffset = '500';
        route.style.transition = 'none';
        pin.style.opacity = '0';
        van.setAttribute('transform', 'translate(60,100)');

        st(() => {
          q('#ss0').classList.remove('active');
          q('#ss0').classList.add('done');
          q('#ss1').classList.add('active');

          route.style.transition = 'stroke-dashoffset 3s linear';
          route.style.strokeDashoffset = '0';

          let start = null;
          const dur = 3000;
          const pathNode = q('#routePath');
          const fallbackLen = 420;

          function animateVan(ts) {
            if (!start) start = ts;
            const prog = Math.min((ts - start) / dur, 1);
            const len = pathNode.getTotalLength ? pathNode.getTotalLength() : fallbackLen;
            const pt = pathNode.getPointAtLength ? pathNode.getPointAtLength(prog * len) : { x: 60 + prog * 400, y: 100 };
            const pt2 = pathNode.getPointAtLength ? pathNode.getPointAtLength(Math.min((prog + 0.02) * len, len)) : { x: pt.x + 5, y: pt.y };
            const angle = Math.atan2(pt2.y - pt.y, pt2.x - pt.x) * 180 / Math.PI;
            van.setAttribute('transform', `translate(${pt.x},${pt.y}) rotate(${angle})`);
            if (prog < 1) requestAnimationFrame(animateVan);
            else {
              van.setAttribute('transform', 'translate(460,90)');
              pin.style.opacity = '1'; pin.style.transition = 'opacity 0.4s';
            }
          }
          requestAnimationFrame(animateVan);

          st(() => {
            q('#ss1').classList.remove('active');
            q('#ss1').classList.add('done');
            q('#ss2').classList.add('active');
          }, 1500);
          st(() => {
            q('#ss2').classList.remove('active');
            q('#ss2').classList.add('done');
            q('#ss3').classList.add('active');
          }, 2700);
          st(() => {
            q('#ss3').classList.remove('active');
            q('#ss3').classList.add('done');
          }, 3500);
        }, 600);
      }

      const clickTargets = [];
      root.querySelectorAll('.step-dot-wrap').forEach((el, idx) => {
        const handler = () => goTo(idx);
        el.addEventListener('click', handler);
        clickTargets.push([el, handler]);
      });
      root.querySelectorAll('.auto-dot').forEach((el, idx) => {
        const handler = () => goTo(idx);
        el.addEventListener('click', handler);
        clickTargets.push([el, handler]);
      });

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !started) {
            started = true;
            goTo(0);
            observer.disconnect();
          }
        },
        { threshold: 0.3 }
      );
      observer.observe(section);

      return () => {
        clearSceneTimers();
        if (autoTimer) clearInterval(autoTimer);
        clickTargets.forEach(([el, handler]) => el.removeEventListener('click', handler));
        observer.disconnect();
      };
    }, []);

    return (
      <section ref={sectionRef} className="how-section-bg py-6 sm:py-10">
        <div ref={rootRef} className="how-root">
          <div className="how-header">
            <h2>How to Order Products</h2>
            <p>Getting your medical, dental, and skincare essentials is easier than ever.</p>
          </div>

          <div className="how-card">
            <div className="step-nav">
              <div className="step-dot-wrap">
                <div className="step-dot active" id="dot0">1</div>
                <div className="step-label active" id="lbl0">Browse &amp; Select</div>
              </div>
              <div className="step-connector"><div className="step-connector-fill" id="fill0" /></div>
              <div className="step-dot-wrap">
                <div className="step-dot" id="dot1">2</div>
                <div className="step-label" id="lbl1">Easy Checkout</div>
              </div>
              <div className="step-connector"><div className="step-connector-fill" id="fill1" /></div>
              <div className="step-dot-wrap">
                <div className="step-dot" id="dot2">3</div>
                <div className="step-label" id="lbl2">Fast Delivery</div>
              </div>
            </div>

            <div className="panels-wrap">
              {/* Panel 1: Browse & Select */}
              <div className="step-panel active" id="panel0">
                <div className="scene-box" id="s1box">
                  <div className="s1-cart-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 01-8 0" />
                    </svg>
                    <span className="s1-badge-count" id="cartCount">0</span>
                  </div>
                  <div className="s1-grid" id="s1grid">
                    <div className="s1-card" id="c0"><div className="s1-icon"><HeartPulse className="w-6 h-6 text-[#1B365D]" strokeWidth={1.5} /></div><div className="s1-name">Dental Mirror Kit</div><div className="s1-price">Rs 350</div></div>
                    <div className="s1-card" id="c1"><div className="s1-icon"><Scissors className="w-6 h-6 text-[#1B365D]" strokeWidth={1.5} /></div><div className="s1-name">Surgical Scissors</div><div className="s1-price">Rs 450</div></div>
                    <div className="s1-card" id="c2"><div className="s1-icon"><Droplets className="w-6 h-6 text-[#1B365D]" strokeWidth={1.5} /></div><div className="s1-name">Hydrating Serum</div><div className="s1-price">Rs 890</div></div>
                    <div className="s1-card" id="c3"><div className="s1-icon"><ShieldPlus className="w-6 h-6 text-[#1B365D]" strokeWidth={1.5} /></div><div className="s1-name">N95 Face Mask</div><div className="s1-price">Rs 45</div></div>
                    <div className="s1-card" id="c4"><div className="s1-icon"><Stethoscope className="w-6 h-6 text-[#1B365D]" strokeWidth={1.5} /></div><div className="s1-name">Digital BP Monitor</div><div className="s1-price">Rs 2,450</div></div>
                    <div className="s1-card" id="c5"><div className="s1-icon"><FlaskConical className="w-6 h-6 text-[#1B365D]" strokeWidth={1.5} /></div><div className="s1-name">Diagnostic Test Kit</div><div className="s1-price">Rs 680</div></div>
                  </div>
                  <div className="s1-cursor" id="s1cursor" style={{ left: "60px", top: "60px" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#1B365D">
                      <path d="M4 0l16 12-7 1-4 7z" />
                    </svg>
                  </div>
                </div>
                <div className="step-desc">
                  <strong>Browse &amp; Select</strong>
                  <span>Explore thousands of medical, dental, surgical and skincare products from trusted brands.</span>
                </div>
              </div>

              {/* Panel 2: Easy Checkout */}
              <div className="step-panel" id="panel1">
                <div className="scene-box" id="s2box">
                  <div className="s2-form">
                    <div className="s2-row">
                      <div className="s2-label">Full name</div>
                      <div className="s2-field" id="s2name" />
                    </div>
                    <div className="s2-row">
                      <div className="s2-label">Phone number</div>
                      <div className="s2-field" id="s2card" />
                    </div>
                    <div className="s2-row">
                      <div className="s2-label">Payment method</div>
                      <div className="s2-methods">
                        <div className="s2-method" id="pm0">Fonepay</div>
                        <div className="s2-method" id="pm1">Cash on Delivery</div>
                      </div>
                    </div>
                    <div className="s2-btn" id="s2btn">Confirm Order</div>
                  </div>
                  <div className="s2-success" id="s2success">
                    <div className="s2-check">✓</div>
                    <p>Order confirmed!</p>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>You'll receive a confirmation shortly</div>
                  </div>
                </div>
                <div className="step-desc">
                  <strong>Easy Checkout</strong>
                  <span>Add items to your cart, choose a payment method, and confirm your order in a few clicks.</span>
                </div>
              </div>

              {/* Panel 3: Fast Delivery */}
              <div className="step-panel" id="panel2">
                <div className="scene-box" id="s3box">
                  <div className="s3-scene">
                    <svg className="s3-map" viewBox="0 0 520 200" id="s3svg">
                      <rect width="520" height="200" fill="#ffffff" />
                      <g stroke="#e5e7eb" strokeWidth={0.5} opacity={0.6}>
                        <line x1="0" y1="40" x2="520" y2="40" />
                        <line x1="0" y1="80" x2="520" y2="80" />
                        <line x1="0" y1="120" x2="520" y2="120" />
                        <line x1="0" y1="160" x2="520" y2="160" />
                        <line x1="80" y1="0" x2="80" y2="200" />
                        <line x1="160" y1="0" x2="160" y2="200" />
                        <line x1="240" y1="0" x2="240" y2="200" />
                        <line x1="320" y1="0" x2="320" y2="200" />
                        <line x1="400" y1="0" x2="400" y2="200" />
                      </g>
                      <path d="M60 100 Q160 100 200 80 Q280 50 360 80 Q420 100 460 90" fill="none" stroke="#d1d5db" strokeWidth={10} strokeLinecap="round" />
                      <path d="M60 100 Q160 100 200 80 Q280 50 360 80 Q420 100 460 90" fill="none" stroke="#f8fafc" strokeWidth={8} strokeLinecap="round" />
                      <path d="M60 100 Q160 100 200 80 Q280 50 360 80 Q420 100 460 90" fill="none" stroke="#e5e7eb" strokeWidth={1} strokeDasharray="8 8" strokeLinecap="round" />
                      <path id="routePath" d="M60 100 Q160 100 200 80 Q280 50 360 80 Q420 100 460 90" fill="none" stroke="#ca8a04" strokeWidth={3} strokeLinecap="round" strokeDasharray="500" strokeDashoffset="500" />
                      <rect x="40" y="84" width="28" height="24" rx="3" fill="#fef9c3" stroke="#1B365D" strokeWidth={1.5} />
                      <rect x="48" y="96" width="12" height="12" rx="1" fill="#1B365D" />
                      <text x="54" y="76" textAnchor="middle" fontSize="9" fill="#6b7280">Warehouse</text>
                      <g id="destPin" opacity={0}>
                        <circle cx="460" cy="90" r="14" fill="#fef9c3" stroke="#ca8a04" strokeWidth={1.5} />
                        <text x="460" y="94" textAnchor="middle" fontSize="13">📍</text>
                      </g>
                      <g id="vanGroup" transform="translate(60,100)">
                        <rect x="-14" y="-9" width="28" height="16" rx="3" fill="#1B365D" />
                        <rect x="-14" y="-9" width="10" height="10" rx="2" fill="#b5d4f4" />
                        <circle cx="-8" cy="7" r="4" fill="#2c2c2a" />
                        <circle cx="-8" cy="7" r="2" fill="#d3d1c7" />
                        <circle cx="8" cy="7" r="4" fill="#2c2c2a" />
                        <circle cx="8" cy="7" r="2" fill="#d3d1c7" />
                      </g>
                    </svg>
                    <div className="s3-status-bar" id="s3statusbar">
                      <div className="s3-status-item active" id="ss0"><div className="s3-dot-s" />Processing</div>
                      <div className="s3-arrow">›</div>
                      <div className="s3-status-item" id="ss1"><div className="s3-dot-s" />Shipped</div>
                      <div className="s3-arrow">›</div>
                      <div className="s3-status-item" id="ss2"><div className="s3-dot-s" />Out for delivery</div>
                      <div className="s3-arrow">›</div>
                      <div className="s3-status-item" id="ss3"><div className="s3-dot-s" />Delivered ✓</div>
                    </div>
                  </div>
                </div>
                <div className="step-desc">
                  <strong>Fast Delivery</strong>
                  <span>Get genuine products delivered safely and quickly to your doorstep across Nepal.</span>
                </div>
              </div>
            </div>

            <div className="auto-bar">
              <div className="auto-dot active" id="adot0" />
              <div className="auto-dot" id="adot1" />
              <div className="auto-dot" id="adot2" />
            </div>
          </div>
        </div>
      </section>
    );
  };

  // Main render function to organize all sections
  const renderPageContent = () => {
    return (
      <>
        <SEOHead
          title="CureHive Nepal"
          description={homepageSettings.featuredProductsSubtitle}
          type="website"
        />

        {/* Hero Section */}
        <div className="w-full md:container mx-auto px-2 sm:px-4 mt-2 md:mt-4 pb-3 md:pb-4">
          <div className="rounded-lg overflow-hidden shadow-lg">
            {heroSlides.length > 0 ? (
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                className="hero-swiper"
              >
                {heroSlides.map((slide, index) => (
                  <SwiperSlide key={index}>
                    <div className="relative">
                      <img
                        src={getImageUrl(slide.image)}
                        alt={slide.title}
                        className="w-full h-32 xs:h-40 sm:h-48 md:h-64 lg:h-80 object-fill"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="relative">
                <img
                  src="/images/curehive1.png"
                  alt="Medical Supplies"
                  className="w-full h-32 xs:h-40 sm:h-48 md:h-64 lg:h-80 object-fill"
                />
              </div>
            )}
          </div>
        </div>

        {/* Trust Badges Section */}
        <div ref={trustBadgesRef} className="hidden md:block w-full md:container mx-auto px-2 sm:px-4 mb-6">
          <div className="bg-[#F8F9FA] rounded-2xl py-3 px-6 sm:px-10 flex flex-wrap justify-between items-center gap-y-4 shadow-sm border border-gray-100 relative">
            <div
              className={`group flex items-center gap-3 w-full sm:w-auto sm:flex-1 justify-start sm:justify-center relative transition-all duration-700 ease-out ${isTrustBadgesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                }`}
              style={{ transitionDelay: "0ms" }}
            >
              <Package className="w-8 h-8 text-gray-800 transition-colors duration-300 group-hover:text-red-600" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-red-600 font-bold text-sm sm:text-base leading-tight">
                  <AnimatedCount target={stats?.productsCount !== undefined ? stats.productsCount : 20000} start={isTrustBadgesVisible} suffix="+" />
                </span>
                <span className="text-gray-800 text-xs sm:text-sm font-medium leading-tight">Products</span>
              </div>
              {/* Vertical divider */}
              <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-8 bg-gray-200"></div>
            </div>

            <div
              className={`group flex items-center gap-3 w-full sm:w-auto sm:flex-1 justify-start sm:justify-center relative transition-all duration-700 ease-out ${isTrustBadgesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                }`}
              style={{ transitionDelay: "120ms" }}
            >
              <Award className="w-8 h-8 text-gray-800 transition-colors duration-300 group-hover:text-red-600" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-red-600 font-bold text-sm sm:text-base leading-tight">
                  <AnimatedCount target={stats?.brandsCount !== undefined ? stats.brandsCount : 450} start={isTrustBadgesVisible} suffix="+" />
                </span>
                <span className="text-gray-800 text-xs sm:text-sm font-medium leading-tight">Trusted Brands</span>
              </div>
              <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-8 bg-gray-200"></div>
            </div>

            <div
              className={`group flex items-center gap-3 w-full sm:w-auto sm:flex-1 justify-start sm:justify-center relative transition-all duration-700 ease-out ${isTrustBadgesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                }`}
              style={{ transitionDelay: "240ms" }}
            >
              <CheckCircle2 className="w-8 h-8 text-gray-800 transition-colors duration-300 group-hover:text-red-600" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-red-600 font-bold text-sm sm:text-base leading-tight">
                  <AnimatedCount target={100} start={isTrustBadgesVisible} suffix="%" />
                </span>
                <span className="text-gray-800 text-xs sm:text-sm font-medium leading-tight">Original</span>
              </div>
              <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2 w-px h-8 bg-gray-200"></div>
            </div>

            <div
              className={`group flex items-center gap-3 w-full sm:w-auto sm:flex-1 justify-start sm:justify-center transition-all duration-700 ease-out ${isTrustBadgesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                }`}
              style={{ transitionDelay: "360ms" }}
            >
              <ShieldCheck className="w-8 h-8 text-gray-800 transition-colors duration-300 group-hover:text-red-600" strokeWidth={1.5} />
              <div className="flex flex-col">
                <span className="text-red-600 font-bold text-sm sm:text-base leading-tight">Assured</span>
                <span className="text-gray-800 text-xs sm:text-sm font-medium leading-tight">Best Prices</span>
              </div>
            </div>
          </div>
        </div>

        {/* Brands Section */}
        {homepageSettings.showBrands && (
          <BrandsSection
            brands={brandsData}
            isLoading={isLoading || isBrandsLoading}
          />
        )}

        {/* Top Categories Section - top-level categories plus any category
            (at any depth) flagged as featured, so a featured subcategory
            still surfaces here even though it isn't top-level */}
        <FeaturedCategoriesSection
          title="Top Categories"
          categories={[
            ...(categoriesApiData?.topLevelCategories || []),
            ...(categoriesApiData?.featured || []).filter(
              (featuredCat) => !(categoriesApiData?.topLevelCategories || []).some(
                (topCat) => topCat._id === featuredCat._id
              )
            )
          ]}
          isLoading={isLoading || isCategoriesLoading}
        />

        {/* Featured Categories Section */}
        <FeaturedCategoryProducts
          title="Featured Categories"
          categories={featuredCategories || []}
          isLoading={isLoading || isFeaturedCategoriesLoading}
        />

        {/* Featured Products Section */}
        <FeaturedProductsSection
          title={homepageSettings.featuredProductsTitle}
          subtitle={homepageSettings.featuredProductsSubtitle}
          products={featuredProductsData}
          isLoading={isLoading || isFeaturedProductsLoading}
        />

        {/* 16:3 Banner Section */}
        {homepageSettings.showBanner && (homepageSettings.bannerImage || homepageSettings.bannerImage2) && (
          <section className="bg-white py-6 sm:py-8 md:py-10 lg:py-12">
            <div className="md:container mx-auto px-2 sm:px-4">
              <div className="relative w-full aspect-[16/3] rounded-lg overflow-hidden shadow-lg">
                {homepageSettings.bannerImage && homepageSettings.bannerImage2 ? (
                  <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    spaceBetween={0}
                    slidesPerView={1}
                    navigation
                    pagination={{ clickable: true }}
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    className="w-full h-full"
                  >
                    <SwiperSlide>
                      <img
                        src={getImageUrl(homepageSettings.bannerImage)}
                        alt="Medical Equipment Banner 1"
                        className="w-full h-full object-fill"
                      />
                    </SwiperSlide>
                    <SwiperSlide>
                      <img
                        src={getImageUrl(homepageSettings.bannerImage2)}
                        alt="Medical Equipment Banner 2"
                        className="w-full h-full object-fill"
                      />
                    </SwiperSlide>
                  </Swiper>
                ) : (
                  <img
                    src={getImageUrl(homepageSettings.bannerImage || homepageSettings.bannerImage2)}
                    alt="Medical Equipment Banner"
                    className="w-full h-full object-fill"
                  />
                )}
              </div>
            </div>
          </section>
        )}

        {/* Category Sections */}
        <CategorySections
          categorySections={categorySections}
          isLoading={isLoading}
        />

        {/* Brand Sections */}
        <BrandSections brandSections={brandSections} isLoading={isLoading} />

        {/* New Arrivals Section */}
        <NewArrivalsSection
          title={homepageSettings.newArrivalsTitle}
          subtitle={homepageSettings.newArrivalsSubtitle}
          products={newArrivalsData}
          isLoading={isLoading || isNewArrivalsLoading}
        />

        {/* How to Order Section */}
        <HowToOrderSection />

        {/* Newsletter Section */}
        {/* {homepageSettings.showNewsletter && (
          <NewsletterSection
            title={homepageSettings.newsletterTitle}
            subtitle={homepageSettings.newsletterSubtitle}
          />
        )} */}
      </>
    );
  };

  return renderPageContent();
};

// Main HomePage component with Suspense and Error Boundary
import { Package, Award, CheckCircle2, ShieldCheck } from "lucide-react";

const HomePage = () => {
  return (
    <HomePageErrorBoundary>
      <Suspense fallback={<HomePageSkeleton />}>
        <HomePageContent />
      </Suspense>
    </HomePageErrorBoundary>
  );
};

export default HomePage;
